import { useMemo, useState } from 'react'
import { apiPatch } from '../../lib/api'
import type { Chat, TeamMember } from '../../lib/types'
import { Avatar } from '../common'
import { nameOf } from './helpers'

// Gestión de un grupo: renombrar, alta/baja de miembros y nombrar admins (solo
// admins del grupo); salir del grupo (cualquiera). El doc del chat llega por el
// listener de la bandeja, así que la UI se refresca sola tras cada PATCH.

export function MembersModal({
  chat,
  meEmail,
  names,
  team,
  onClose,
  onLeft,
  toast,
}: {
  chat: Chat
  meEmail: string
  names: Map<string, string>
  team: TeamMember[]
  onClose: () => void
  onLeft: () => void
  toast: (msg: string) => void
}) {
  const isAdmin = chat.admins.includes(meEmail)
  const [busy, setBusy] = useState(false)
  const [name, setName] = useState(chat.name ?? '')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [addQuery, setAddQuery] = useState('')

  const patch = async (body: Record<string, unknown>, errMsg: string): Promise<boolean> => {
    setBusy(true)
    try {
      await apiPatch(`/api/chats/${chat.id}`, body)
      return true
    } catch {
      toast(errMsg)
      return false
    } finally {
      setBusy(false)
    }
  }

  const candidates = useMemo(
    () =>
      team
        .filter((m) => m.isActive !== false && !chat.members.includes(m.email))
        .filter(
          (m) =>
            !addQuery ||
            m.name.toLowerCase().includes(addQuery.toLowerCase()) ||
            m.email.toLowerCase().includes(addQuery.toLowerCase()),
        ),
    [team, chat.members, addQuery],
  )

  const toggle = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(email)) next.delete(email)
      else next.add(email)
      return next
    })
  }

  const addSelected = async () => {
    if (selected.size === 0) return
    if (await patch({ addMembers: [...selected] }, 'No se pudieron agregar los miembros')) {
      setSelected(new Set())
      setAddQuery('')
    }
  }

  const leave = async () => {
    if (!window.confirm('¿Salir de este grupo?')) return
    if (await patch({ leave: true }, 'No se pudo salir del grupo')) onLeft()
  }

  return (
    <div className="chat-modal-overlay modal-overlay" role="presentation" data-capa-flotante onClick={onClose}>
      <div
        className="chat-modal modal"
        role="dialog"
        aria-modal="true"
        aria-label="Miembros del grupo"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chat-modal-head">
          <h2>{chat.name ?? 'Grupo'}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Cerrar">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isAdmin && (
          <div className="chat-modal-row">
            <label className="field-label" htmlFor="chat-group-name">
              Nombre del grupo
            </label>
            <div className="chat-inline-form">
              <input
                id="chat-group-name"
                value={name}
                maxLength={80}
                onChange={(e) => setName(e.target.value)}
              />
              {name.trim() !== (chat.name ?? '') && name.trim() && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => void patch({ name: name.trim() }, 'No se pudo renombrar el grupo')}
                >
                  Guardar
                </button>
              )}
            </div>
          </div>
        )}

        <div className="chat-modal-row">
          <p className="field-label">Miembros ({chat.members.length})</p>
          <ul className="chat-member-list">
            {chat.members.map((email) => {
              const memberIsAdmin = chat.admins.includes(email)
              return (
                <li key={email} className="chat-member-row">
                  <Avatar who={nameOf(names, email)} title={email} />
                  <span className="chat-member-name">
                    {nameOf(names, email)}
                    {email === meEmail && <span className="muted"> (tú)</span>}
                  </span>
                  {memberIsAdmin && <span className="tag">admin</span>}
                  {isAdmin && !memberIsAdmin && (
                    <button
                      type="button"
                      className="btn btn-ghost chat-mini-btn"
                      disabled={busy}
                      onClick={() => void patch({ addAdmins: [email] }, 'No se pudo nombrar admin')}
                    >
                      Hacer admin
                    </button>
                  )}
                  {isAdmin && email !== meEmail && (
                    <button
                      type="button"
                      className="icon-btn danger"
                      disabled={busy}
                      title="Quitar del grupo"
                      aria-label={`Quitar a ${nameOf(names, email)}`}
                      onClick={() => void patch({ removeMembers: [email] }, 'No se pudo quitar al miembro')}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        </div>

        {isAdmin && (
          <div className="chat-modal-row">
            <p className="field-label">Agregar miembros</p>
            <div className="search">
              <span aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
              </span>
              <input
                value={addQuery}
                onChange={(e) => setAddQuery(e.target.value)}
                placeholder="Buscar persona…"
                aria-label="Buscar persona para agregar"
              />
            </div>
            <ul className="chat-member-list chat-pick-list">
              {candidates.length === 0 && <li className="muted chat-pick-empty">Sin resultados</li>}
              {candidates.map((m) => (
                <li key={m.email}>
                  <label className="chat-pick-row">
                    <input
                      type="checkbox"
                      checked={selected.has(m.email)}
                      onChange={() => toggle(m.email)}
                    />
                    <Avatar who={m.name} title={m.email} />
                    <span className="chat-member-name">{m.name}</span>
                    <span className="muted">{m.email}</span>
                  </label>
                </li>
              ))}
            </ul>
            {selected.size > 0 && (
              <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void addSelected()}>
                Agregar {selected.size} {selected.size === 1 ? 'persona' : 'personas'}
              </button>
            )}
          </div>
        )}

        <div className="chat-modal-foot">
          <button type="button" className="btn btn-danger" disabled={busy} onClick={() => void leave()}>
            Salir del grupo
          </button>
        </div>
      </div>
    </div>
  )
}
